// ─────────────────────────────────────────────────────────────────────
// Centralized date / time formatters — Iter 238
//
// Single source of truth for ALL date+time display in the app. Replaces
// 200+ ad-hoc `toLocaleString()` / `toLocaleDateString()` / `slice(0, 10)`
// calls that produced inconsistent formats and dependent on the browser
// locale defaulting (en-US vs en-PH vs en-GB).
//
// Display contract:
//   • Date  →  MM/DD/YYYY                  (e.g. 05/05/2026)
//   • Time  →  hh:mm AM/PM                 (e.g. 08:30 AM)
//   • Both  →  MM/DD/YYYY · hh:mm AM/PM    (e.g. 05/05/2026 · 08:30 AM)
//
// All formatters accept a UTC ISO string (or a `Date`, or an empty
// value) and render in the org's local timezone (cached in localStorage
// under `agribooks.org_tz`, defaulting to Asia/Manila).
// ─────────────────────────────────────────────────────────────────────

const DEFAULT_TZ = 'Asia/Manila';

function _tz() {
  try {
    return localStorage.getItem('agribooks.org_tz') || DEFAULT_TZ;
  } catch {
    return DEFAULT_TZ;
  }
}

function _toDate(input) {
  if (!input) return null;
  if (input instanceof Date) return isNaN(input.getTime()) ? null : input;
  // If string is a YYYY-MM-DD calendar-day (no time component), parse as
  // local-day at midnight to avoid the toISOString TZ-shift trap.
  if (typeof input === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(input)) {
    const [y, m, d] = input.split('-').map(Number);
    return new Date(y, m - 1, d);
  }
  const d = new Date(input);
  return isNaN(d.getTime()) ? null : d;
}

/** MM/DD/YYYY in org-local TZ. Returns '' for falsy input. */
export function formatDate(input) {
  const d = _toDate(input);
  if (!d) return '';
  try {
    return d.toLocaleDateString('en-US', {
      timeZone: _tz(),
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    });
  } catch {
    return d.toLocaleDateString('en-US');
  }
}

/** hh:mm AM/PM in org-local TZ. Returns '' for falsy input. */
export function formatTime(input) {
  const d = _toDate(input);
  if (!d) return '';
  try {
    return d.toLocaleTimeString('en-US', {
      timeZone: _tz(),
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  } catch {
    return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
  }
}

/** "Sep 16, 4:08 PM" in org-local TZ — the compact stamp for card timelines,
 *  where MM/DD/YYYY · hh:mm is too wide to sit under every step. The year is
 *  added only when it is not the current one. Returns '' for falsy input. */
export function formatDateTimeShort(input) {
  const d = _toDate(input);
  if (!d) return '';
  try {
    const tz = _tz();
    const sameYear = d.toLocaleDateString('en-US', { timeZone: tz, year: 'numeric' })
      === new Date().toLocaleDateString('en-US', { timeZone: tz, year: 'numeric' });
    const date = d.toLocaleDateString('en-US', {
      timeZone: tz, month: 'short', day: 'numeric', ...(sameYear ? {} : { year: 'numeric' }),
    });
    const time = d.toLocaleTimeString('en-US', {
      timeZone: tz, hour: 'numeric', minute: '2-digit', hour12: true,
    });
    return `${date}, ${time}`;
  } catch {
    return formatDateTime(d);
  }
}

/** MM/DD/YYYY · hh:mm AM/PM in org-local TZ. Returns '' for falsy input. */
export function formatDateTime(input) {
  const d = _toDate(input);
  if (!d) return '';
  const date = formatDate(d);
  const time = formatTime(d);
  return date && time ? `${date} · ${time}` : (date || time);
}

/** hh:mm:ss AM/PM (used for live clocks / very precise audit logs). */
export function formatTimeFull(input) {
  const d = _toDate(input);
  if (!d) return '';
  try {
    return d.toLocaleTimeString('en-US', {
      timeZone: _tz(),
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
  } catch {
    return d.toLocaleTimeString('en-US', { hour12: true });
  }
}

/** "May 5, 2026" — long-form date, used in section headers / report titles. */
export function formatDateLong(input) {
  const d = _toDate(input);
  if (!d) return '';
  try {
    return d.toLocaleDateString('en-US', {
      timeZone: _tz(),
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return d.toLocaleDateString('en-US');
  }
}

/** Today's date in org-local TZ as YYYY-MM-DD (canonical storage format).
 *  Replaces ad-hoc `new Date().toISOString().slice(0, 10)` which used UTC
 *  and produced yesterday-Manila for ~8 hours per day. */
export function localTodayStr() {
  const tz = _tz();
  try {
    // 'en-CA' yields YYYY-MM-DD natively.
    return new Date().toLocaleDateString('en-CA', { timeZone: tz });
  } catch {
    return new Date().toLocaleDateString('en-CA');
  }
}

/** Calendar day after the given YYYY-MM-DD, using pure string arithmetic.
 *  Avoids the `new Date(s).toISOString().slice(0,10)` UTC trap that shifts
 *  one day on devices whose JS-engine TZ differs from the org TZ. Returns
 *  '' for malformed input. */
export function nextCalendarDay(ymd) {
  if (!ymd || typeof ymd !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return '';
  // Construct a UTC-midnight Date from the components, add 24h, format.
  // Using UTC throughout (not local) means no TZ shift can happen.
  const [y, m, d] = ymd.split('-').map(Number);
  const utc = new Date(Date.UTC(y, m - 1, d));
  utc.setUTCDate(utc.getUTCDate() + 1);
  const yy = utc.getUTCFullYear();
  const mm = String(utc.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(utc.getUTCDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

/** Convert a server-issued UTC ISO string into the wall-clock time
 *  string the user actually saw at that moment. Used for the sales_log
 *  "time" column and any place we display a recorded time. */
export function timeFromIso(iso) {
  return formatTime(iso);
}

/** Convert a `time_str` ("HH:MM:SS" 24h) into "hh:mm AM/PM". */
export function format24To12(timeStr) {
  if (!timeStr || typeof timeStr !== 'string') return '';
  const m = timeStr.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?/);
  if (!m) return timeStr;
  let h = parseInt(m[1], 10);
  const min = m[2];
  if (Number.isNaN(h)) return timeStr;
  const period = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  return `${String(h).padStart(2, '0')}:${min} ${period}`;
}

/** Repair the year segment of a date typed into an `<input type="date">`.
 *
 *  The native segmented editor accepts a bare "26" in the year box and stores
 *  it as `0026`. Prod collected three of these on DR dates (`0026-08-24`,
 *  `0026-08-04`, `0006-08-13`) — and a DR date orders the capital timeline, so
 *  a year typo sorts that receipt before every other lot and hands the
 *  last-purchase slot to the wrong one.
 *
 *  A 1- or 2-digit year is read as 20xx (the standard two-digit convention:
 *  26 -> 2026). Three-digit and other malformed years are returned UNCHANGED
 *  so the caller's min/max check rejects them visibly — guessing at `0006`
 *  would just replace a loud error with a quiet wrong date.
 */
export function normalizeTypedYear(iso) {
  const m = /^(\d{1,4})-(\d{2})-(\d{2})$/.exec(String(iso || ''));
  if (!m) return iso;
  const y = parseInt(m[1], 10);
  if (y >= 1 && y < 100) return `20${String(y).padStart(2, '0')}-${m[2]}-${m[3]}`;
  return iso;
}

/** YYYY-MM-DD `n` years before today (org-local). Used as the `min` on
 *  backdated-document inputs so an out-of-range year can't be typed past. */
export function yearsAgoStr(n) {
  const t = localTodayStr();
  const [y, rest] = [t.slice(0, 4), t.slice(4)];
  return `${String(Number(y) - n).padStart(4, '0')}${rest}`;
}

const _api = {
  formatDate,
  formatTime,
  formatDateTime,
  formatTimeFull,
  formatDateLong,
  localTodayStr,
  normalizeTypedYear,
  yearsAgoStr,
  nextCalendarDay,
  timeFromIso,
  format24To12,
};
export default _api;
